import { Component, h } from 'preact';
import {
  AmDurationType,
  AmHolidayType,
  AmLocation,
  Day,
  Month,
  NBSP,
  Price,
  Prices,
  Properties,
  TravelDates,
  TravelType,
} from '../../../../common';
import { WeekdayKey } from '../../../../common/calendar';
import {
  DateFormat,
  DateText,
  WeekdayFormat,
  PriceFormatOptions,
  PriceFormatProps,
  WeekdayText,
} from '../../../../i18n/Format';
import { WithStyles, withStyles } from '../../../../style';
import { debounceCallback, isBlank, isPresent, stopPropagation, values } from '../../../../utils';
import * as styles from './CalendarMonth.scss';
import { amDayIsAvailable, amDayIsDisabled } from '../dateModalUtils';
import { AmAvailability } from '../../../../store';
import { CalendarDay } from '../CalendarDay/CalendarDay';
import { getLang } from '../../../../i18n/getLang';

const priceFormatOptions: PriceFormatOptions = Object.freeze({
  abbreviate: true,
  showThousandSeparators: false,
  showSymbol: true,
  placeholder: NBSP,
  trimWhitespace: true,
  useCalendarOptions: true,
});

const findPriceForDay = (prices: Prices, date: string): Price => (prices?.[date]?.amount ? prices[date] : null);

const getCheapestPricePerYear = (prices: Prices) => {
  return prices
    ? Math.min(
        ...values(prices)
          .filter((day) => day.amount)
          .map((day) => +day.amount)
      )
    : null;
};

const getMonth = (date: string): string => {
  return isPresent(date) ? date.substr(5, 2) : undefined;
};

const inBetweenOrEqualDay = ({ id }: Day, departureDate: string, returnDate: string): boolean => {
  if (isBlank(departureDate) || isBlank(returnDate)) {
    return false;
  }
  return departureDate < id && id <= returnDate;
};

const inBetweenMonth = ({ id }: Month, departureDate: string, returnDate: string) => {
  if (isBlank(departureDate) || isBlank(returnDate)) {
    return false;
  }
  return getMonth(departureDate) < getMonth(id) && getMonth(id) < getMonth(returnDate);
};

const addDays = (isoDate = '', days: number): string =>
  new Date(new Date(isoDate.substr(0, 10)).getTime() + 1000 * 60 * 60 * 24 * days).toISOString().substr(0, 10);

export interface CalendarMonthProps extends Properties {
  month: Month;
  weekdays: WeekdayKey[];
  travelDates: TravelDates;
  travelType: TravelType;
  fullYear?: Prices;
  followingMonth?: Prices;
  onSelectDate: (date: string) => void;
  onAmTab: boolean;
  amHolidayType?: AmHolidayType;
  amDestination?: AmLocation;
  amAvailability?: AmAvailability;
  amDuration?: string;
  onHoverEnd: (day: string) => void;
  hoverDay: string;
  showPrices: boolean;
  locale: string;
  listId: number;
}

export interface CalendarMonthState {}

@withStyles(styles)
export default class CalendarMonth extends Component<CalendarMonthProps, CalendarMonthState> {
  timeoutHook: ReturnType<typeof setTimeout>;

  onHoverEndDebounced = debounceCallback((day: string) => {
    this.props.onHoverEnd(day);
    this.setState((prevState) => ({ ...prevState, hoverDay: day }));
  }, 5);

  componentWillUnmount(): void {
    clearTimeout(this.timeoutHook);
  }

  differenceToCheapestReturn = (day: string): Price => {
    const { followingMonth } = this.props;

    return findPriceForDay(followingMonth, day);
  };

  calculateStartingPrice = (day: string): PriceFormatProps => {
    const {
      travelDates: { departureDate, returnDate },
      travelType,
      fullYear,
    } = this.props;

    const diffPrice: PriceFormatProps = {
      price: this.differenceToCheapestReturn(day),
      options: priceFormatOptions,
    };
    const dayPrice: PriceFormatProps = {
      price: findPriceForDay(fullYear, day),
      options: priceFormatOptions,
    };

    if (departureDate && !returnDate && travelType === TravelType.ROUND_TRIP) {
      return diffPrice;
    }

    if (departureDate && returnDate) {
      if (departureDate === day) {
        return { ...dayPrice, price: null };
      }
      if (returnDate === day) {
        return diffPrice;
      }
      if (departureDate < day && returnDate > day) {
        return { ...dayPrice, price: null };
      }
    }

    return dayPrice;
  };

  calculateAvailability = (day: string, monthlyPriceLimitDay: string | undefined): boolean => {
    const {
      travelDates: { departureDate, returnDate },
      travelType,
      fullYear,
    } = this.props;

    const diffPrice = this.differenceToCheapestReturn(day);
    const dayPrice = findPriceForDay(fullYear, day);

    if (departureDate && !returnDate && travelType === TravelType.ROUND_TRIP) {
      // allow green lights for selected departure date and days exceeding the monthlyPriceLimitDay
      return (departureDate <= day && !!diffPrice) || day > monthlyPriceLimitDay || day === departureDate;
    }

    return !!dayPrice;
  };

  calculateCheapestPrice = (): number | null => {
    const {
      travelDates: { departureDate, returnDate },
      fullYear,
      travelType,
      followingMonth,
    } = this.props;

    if (departureDate && !returnDate && travelType === TravelType.ROUND_TRIP) {
      return getCheapestPricePerYear(followingMonth);
    }

    return getCheapestPricePerYear(fullYear);
  };

  select = (e: Event, date: string): false => {
    stopPropagation(e);
    this.props.onSelectDate(date);
    return false;
  };

  onHoverEnd = (day: string): void => {
    this.timeoutHook = this.onHoverEndDebounced(day);
  };

  render({
    month,
    weekdays,
    travelDates,
    travelType,
    onAmTab,
    amHolidayType,
    amDestination,
    amAvailability,
    amDuration,
    tx,
    hoverDay,
    fullYear,
    showPrices,
    locale,
  }: CalendarMonthProps & WithStyles): h.JSX.Element {
    const { departureDate, returnDate } = travelDates;
    const departureMonth: boolean = getMonth(month.id) === getMonth(departureDate);
    const returnMonth: boolean = getMonth(month.id) === getMonth(returnDate);
    const fillerMonth: boolean = inBetweenMonth(month, departureDate, returnDate);
    const hasPrices = fullYear ? values(fullYear).some((price) => !!price?.amount) : false;
    const monthlyPriceLimitDay = departureDate ? addDays(departureDate, 17) : undefined;
    const cheapestPrice = this.calculateCheapestPrice();

    return (
      <div
        // wrapper for title & selection styling
        class={tx(this.props.class, 'monthWrapper', {
          departureMonth,
          returnMonth,
          fillerMonth,
          oneWay: travelType === TravelType.ONE_WAY,
        })}
        data-month-id={this.props.listId}
      >
        {/* Month title */}
        <div class={tx('title', 'flex flex--row font-body-1 nordic-blue-900-text bold-type ps-large')}>
          <span class="ps-xsmall-r">
            <DateText value={month.id} format={DateFormat.MONTH} />
          </span>
          <span>
            <DateText value={month.id} format={DateFormat.YEAR} />
          </span>
        </div>

        {/* Actual calendar month */}
        <div className={tx('calendarMonth', 'ps-large-x')}>
          {weekdays.map((dayKey: WeekdayKey, i: number) => (
            <div
              key={dayKey}
              class={
                getLang(locale) === 'fi' ? tx('weekDay', 'font-body-2') : tx('weekDay', 'font-body-2', 'capitalize')
              }
              style={{ msGridColumn: String(i + 1), msGridRow: '1' }}
            >
              <WeekdayText value={dayKey} format={WeekdayFormat.SHORT} />
            </div>
          ))}
          {month.days.map((day: Day, i: number) => {
            const isDepartureDate: boolean = day.id === departureDate;
            const isReturnDate: boolean =
              day.id === returnDate || (travelType === TravelType.ONE_WAY && day.id === departureDate);
            const isSelected: boolean = isDepartureDate || isReturnDate;
            const { price, options } = this.calculateStartingPrice(day.id);
            const isDisabled =
              onAmTab && amDayIsDisabled(day, departureDate, returnDate, amDestination, amHolidayType, amAvailability);
            const isAvailable =
              onAmTab &&
              amHolidayType?.type === AmDurationType.LIST &&
              amDayIsAvailable(day, amAvailability, amDuration);

            const hasAvailability = this.calculateAvailability(day.id, monthlyPriceLimitDay);
            const isHovered =
              inBetweenOrEqualDay(day, departureDate, hoverDay) &&
              travelType === TravelType.ROUND_TRIP &&
              !returnDate &&
              !!departureDate;
            const isHoverEnd =
              hoverDay === day.id &&
              travelType === TravelType.ROUND_TRIP &&
              !returnDate &&
              !!departureDate &&
              hoverDay > departureDate;
            const isCheapestDay = price?.amount && cheapestPrice && +price.amount === cheapestPrice;

            return (
              <CalendarDay
                key={i}
                index={i}
                day={day}
                departureDate={departureDate}
                showAvailability={!onAmTab && hasPrices && showPrices}
                hasAvailability={hasAvailability}
                isAvailable={isAvailable}
                isDepartureDate={isDepartureDate}
                isDisabled={isDisabled}
                isReturnDate={isReturnDate}
                isSelected={isSelected}
                options={options}
                isHovered={isHovered}
                isHoverEnd={isHoverEnd}
                price={price}
                showPrice={showPrices}
                returnDate={returnDate}
                onSelect={this.select}
                onHoverEnd={this.onHoverEnd}
                highlightCheapestPrice={isCheapestDay}
              />
            );
          })}
        </div>
      </div>
    );
  }
}
